import { SplitFactory } from '@splitsoftware/splitio';
import type { App } from 'vue';
import type { SplitConfig, SplitServiceInterface, SplitUserAttributes, TreatmentValue } from '@/types/split';

class SplitService implements SplitServiceInterface {
  client: any = null;
  isReady: boolean = false;
  userConfig: SplitConfig | null = null;

  async init(config: SplitConfig): Promise<void> {
    if (!config.users || !config.users.id) {
      console.warn('🎯 Split.io: Cannot initialize - User ID required');
      return Promise.reject('User ID required');
    }


    this.userConfig = config;
    const authorizationKey = this.getEnvironmentKey();
    
    
    try {
      // Always use Split.io service (local dev points to stage)
      await this.initWithSplitService(authorizationKey, config);
    } catch (error) {
      console.error('🎯 Split.io: Initialization failed:', error);
      throw error;
    }
  }

  private async initWithSplitService(authorizationKey: string, config: SplitConfig): Promise<void> {
    const factory = SplitFactory({
      core: {
        authorizationKey: authorizationKey,
        key: config.users.id.toString(),
      },
      startup: {
        readyTimeout: 10000, // 10 seconds
      },
      debug: false //set to debug: import.meta.env.DEV to show in local, state, preprod
    });

    this.client = factory.client();
    
    return new Promise((resolve, reject) => {
      this.client.on(this.client.Event.SDK_READY, () => {
        this.isReady = true;
        // Dispatch custom event for components waiting for Split.io
        window.dispatchEvent(new Event('splitLoaded'));
        resolve();
      });

      this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
        console.warn('⚠️ Split.io SDK ready timeout');
        reject(new Error('SDK ready timeout'));
      });
    });
  }


  private getEnvironmentKey(): string {
    // Check for environment variable first (for production builds)
    const envKey = import.meta.env.VITE_SPLIT_API_KEY;
    if (envKey) {
      return envKey;
    }

    // Fall back to hostname detection
    const hostname = window.location.hostname;
    
    // Split.io API keys mapped to environment patterns
    const splitEnvironmentKeys = {
      'local-env': '12ihesumo0bsc303pmll9k5n9qr4k7nbpqrt',  // Use stage for local
      stage: '12ihesumo0bsc303pmll9k5n9qr4k7nbpqrt',
      release: 'uj3r4bs8jtfpegba4pl1qshdbmos5q8f2lkj',  // Preprod
      preprod: 'uj3r4bs8jtfpegba4pl1qshdbmos5q8f2lkj',  // Preprod  
      cert: 'uj3r4bs8jtfpegba4pl1qshdbmos5q8f2lkj',     // Preprod
      ovationcxm: 'com9j39t6sabpehod2vibni3q9ekegsv7ht9',  // Prod
      goboomtown: 'com9j39t6sabpehod2vibni3q9ekegsv7ht9'   // Prod
    };
    
    // For local development, use stage environment
    if (hostname.includes('localhost') || hostname.includes('local-env')) {
      return splitEnvironmentKeys['local-env'];
    } else if (hostname.includes('stage')) {
      return splitEnvironmentKeys.stage;
    } else if (hostname.includes('preprod')) {
      return splitEnvironmentKeys.preprod;
    } else if (hostname.includes('ovationcxm')) {
      return splitEnvironmentKeys.ovationcxm;
    } else if (hostname.includes('goboomtown')) {
      return splitEnvironmentKeys.goboomtown;
    }
    
    // Default to production
    return splitEnvironmentKeys.ovationcxm;
  }

  getSplitAttributes(additionalAttributes: Record<string, any> = {}): SplitUserAttributes {
    const baseAttributes: SplitUserAttributes = {
      userId: this.userConfig?.users?.id || null,
      orgId: this.userConfig?.users_group?.id || null,
      userRole: this.userConfig?.users?.role || null,
      browser: navigator.userAgent,
      privateLabel: this.userConfig?.private_label || null,
    };

    return { ...baseAttributes, ...additionalAttributes };
  }

  getTreatment(splitName: string, attributes: Record<string, any> = {}): TreatmentValue | null {
    if (!this.client || !this.isReady) {
      console.warn(`Split.io not ready for treatment: ${splitName}`);
      return null;
    }

    const splitAttributes = this.getSplitAttributes(attributes);
    const treatment = this.client.getTreatment(splitName, splitAttributes);

    // Log if treatment is 'control' (default when split doesn't exist)
    if (treatment === 'control') {
      console.warn(`Split.io treatment '${splitName}' not found, got 'control'`);
    }

    return treatment;
  }

  // Helper method for on/off feature flags - defaults to false (off) for control
  isFeatureEnabled(splitName: string, attributes: Record<string, any> = {}): boolean {
    const treatment = this.getTreatment(splitName, attributes);
    return treatment === 'on';
  }

  destroy(): void {
    if (this.client) {
      this.client.destroy();
    }
  }
}

// Create singleton instance
let splitServiceInstance: SplitService | null = null;

export function getSplitService(): SplitService {
  if (!splitServiceInstance) {
    splitServiceInstance = new SplitService();
  }
  return splitServiceInstance;
}

// Vue 3 Plugin
export default {
  install(app: App) {
    const splitService = getSplitService();
    
    app.config.globalProperties.$split = splitService;
    app.provide('split', splitService);
  }
};

// Export service class for direct usage
export { SplitService };
